// src/component/Home/OurPartners/OurPartners.jsx
import React, { useEffect } from "react";
import styled from "styled-components";
import siemenslogo from "../../../assets/images/siemens.png";

/* ========= STYLES (Sonata-inspired layout, your data) ========= */
const Section = styled.section`
  position: relative;
  padding: 90px 20px;
  font-family: "Poppins", sans-serif;
  background:
    radial-gradient(140% 120% at 0% 0%, #083247 0%, #062c3f 55%, #052433 100%);
  overflow: hidden;
  @media (max-width: 992px){ padding:72px 18px; }
  @media (max-width: 576px){ padding:56px 14px; }

  /* soft ambient halo */
  &::before{
    content:"";
    position:absolute; inset:-10px;
    background: radial-gradient(70% 50% at 50% -10%, rgba(16,181,255,.18), transparent 70%);
    filter: blur(32px);
    pointer-events:none;
  }
`;

const ContentCard = styled.div`
  position: relative;
  border-radius: 20px;
  padding: 28px;
  background: linear-gradient(180deg, rgba(255,255,255,.10), rgba(255,255,255,.06));
  border: 1px solid rgba(255,255,255,.18);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.22), 0 16px 42px rgba(0,0,0,.28);
  @media (max-width: 992px){ margin-bottom: 26px; }
`;

const H = styled.h4`
  font-size: clamp(2rem, 3.2vw, 3rem);
  color: #e6f7ff; font-weight: 700; margin: 0 0 10px;
  span{ color:#00cbf1; }
  &::after{
    content:""; display:block; width:68px; height:3px; margin-top:10px;
    background: linear-gradient(90deg,#00cbf1,#63e2ff); border-radius:999px;
  }
`;

const Sub = styled.h5`
  margin: 14px 0 0;
  font-size: clamp(1.1rem, 1.8vw, 1.4rem);
  color: #7fe7ff; font-weight: 600;
`;

const P = styled.p`
  margin: 16px 0 0; color: #f5fbff;
  font-size: clamp(.98rem, 1.2vw, 1.05rem); line-height: 1.9; opacity:.9; text-align: justify;
`;

const CTA = styled.a`
  display:inline-flex; align-items:center; gap:10px; margin-top:16px;
  color:#bfeaff; text-decoration:none; font-weight:600;
  &:after{ content:"→"; transition: transform .2s ease; }
  &:hover:after{ transform: translateX(4px); }
`;

/* Right: glowing Siemens logo with gentle tilt */
const LogoWrap = styled.div`
  position: relative; display:grid; place-items:center; padding:16px; border-radius:22px; isolation:isolate;

  /* outer aura */
  &::before{
    content:""; position:absolute; inset:-8px; border-radius: inherit;
    background:
      radial-gradient(220px 160px at 50% 40%, rgba(0,203,241,.45), transparent 70%),
      radial-gradient(360px 240px at 50% 60%, rgba(99,226,255,.30), transparent 70%);
    filter: blur(22px); z-index:0; opacity:.85; transition: opacity .2s ease, transform .2s ease;
  }
`;

const Plate = styled.div`
  position: relative; z-index:1;
  background: rgba(255,255,255,.12);
  border: 1px solid rgba(255,255,255,.22);
  border-radius: 20px; padding: clamp(14px, 2.6vw, 22px);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.25), 0 18px 44px rgba(0,0,0,.35);
`;

const PartnerImage = styled.img`
  width: min(82%, 460px); height:auto; display:block; object-fit:contain;
  filter: drop-shadow(0 0 10px rgba(255,255,255,.35)) drop-shadow(0 8px 18px rgba(0,0,0,.35));
  @media (max-width: 992px){ width:min(90%, 440px); }
  @media (max-width: 576px){ width:min(96%, 420px); }
`;

/* micro tilt */
const useTilt = () => {
  useEffect(() => {
    const el = document.querySelector("[data-tilt]");
    if(!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      const rx = -((y - r.height/2)/(r.height/2))*6;
      const ry =  ((x - r.width/2)/(r.width/2))*6;
      el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)`;
    };
    const onLeave = () => { el.style.transform = ""; };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);
};

/* ========= COMPONENT ========= */
const OurPartners = () => {
  useTilt();

  // ⬇️ your data kept intact
  const heading = <>Our <span>Partners</span></>;
  const subtitle = "Partner-driven Hyper Growth";
  const paragraph = `Our partners drive our growth and success. We believe in creating a successful partnership ecosystem that delivers value to our customers. Our partner network is built on the foundation of trust, collaboration, and innovation. We have partnered with some of the leading technology companies to deliver cutting-edge solutions to our customers.`;

  return (
    <Section className="ourpartner-section">
      <div className="container">
        <div className="row align-items-center">
          {/* Left: your copy */}
          <div className="col-lg-6">
            <ContentCard>
              <H>{heading}</H>
              <Sub>{subtitle}</Sub>
              <P>{paragraph}</P>
              {/* optional CTA, points to your alliances/partners page if you have one */}
              <CTA href="/partners">Know more</CTA>
            </ContentCard>
          </div>

          {/* Right: your Siemens image with glow */}
          <div className="col-lg-6 d-flex justify-content-center">
            <LogoWrap data-tilt>
              <Plate>
                <PartnerImage
                  src={siemenslogo}
                  alt="Siemens Partner"
                  loading="lazy"
                  decoding="async"
                />
              </Plate>
            </LogoWrap>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default OurPartners;
